import {
  getEndTimeFromStartAndDurationSeconds,
  normalizePrefillLabel,
  resolvePrefillEntityMatch,
} from 'features/Responder/features/Schedules/features/ScheduleBuilder/utils';
import { useCallback, useEffect, useState } from 'react';

export const useScheduleBuilderTargetSelection = ({
  activeEditSchedule,
  initialPrefill,
  scheduleName,
  setScheduleName,
  startTime,
  useTourDuration,
  tours,
  zones,
  setTours,
  selectedTourIdRef,
  selectedZoneIdRef,
  appliedPrefillKeyRef,
  selectTour,
  focusTourOnMap,
  setActiveZoneId,
  setZoneSelectedZoneIds,
  setMapSelectedZoneIds,
  setSelectedTourIds,
  setCenter,
  hydrateTourIfNeeded,
  getTotalDurationOfTour,
  setEndTime,
  setScheduleDuration,
}) => {
  const [selectedTour, setSelectedTour] = useState(0);
  const [selectedZone, setSelectedZone] = useState(0);

  const selectTourOption = useCallback(
    (value) => {
      const nextValue = Number(value) || 0;
      const tourObj = nextValue > 0 ? tours[nextValue - 1] || null : null;
      selectedTourIdRef.current = tourObj?.id ?? null;
      selectedZoneIdRef.current = null;
      setSelectedTour(nextValue);
      if (nextValue > 0) setSelectedZone(0);
      selectTour(tourObj);
      focusTourOnMap(tourObj);
      setActiveZoneId(null);
      setZoneSelectedZoneIds([]);
      setMapSelectedZoneIds([]);
      setSelectedTourIds(tourObj?.id != null ? [tourObj.id] : []);
      hydrateTourIfNeeded(tourObj);
      if (tourObj && useTourDuration) {
        const minDurationSec = getTotalDurationOfTour(tourObj);
        const starts = Array.isArray(startTime) ? startTime : [startTime];
        const ends = starts.map((st) => {
          if (typeof st === 'string' && st.includes(':')) {
            return getEndTimeFromStartAndDurationSeconds(st, minDurationSec);
          }
          return '';
        });
        setEndTime(ends);
        setScheduleDuration(Math.ceil(minDurationSec / 60));
      }
    },
    [
      focusTourOnMap,
      getTotalDurationOfTour,
      hydrateTourIfNeeded,
      selectTour,
      setActiveZoneId,
      setEndTime,
      setMapSelectedZoneIds,
      setScheduleDuration,
      setSelectedTourIds,
      setZoneSelectedZoneIds,
      startTime,
      tours,
      useTourDuration,
      selectedTourIdRef,
      selectedZoneIdRef,
    ],
  );

  const selectZoneOption = useCallback(
    (value) => {
      const nextValue = Number(value) || 0;
      setSelectedZone(nextValue);
      if (nextValue > 0) setSelectedTour(0);
      selectTour(null);
      if (nextValue > 0) {
        setCenter(null);
      }
      const zoneId = nextValue > 0 ? (zones[nextValue - 1]?.id ?? nextValue) : null;
      selectedTourIdRef.current = null;
      selectedZoneIdRef.current = zoneId != null ? zoneId : null;
      setActiveZoneId(zoneId);
      setZoneSelectedZoneIds(zoneId != null ? [zoneId] : []);
      setMapSelectedZoneIds(zoneId != null ? [zoneId] : []);
      setSelectedTourIds([]);
    },
    [
      selectTour,
      setActiveZoneId,
      setCenter,
      setMapSelectedZoneIds,
      setSelectedTourIds,
      setZoneSelectedZoneIds,
      zones,
      selectedTourIdRef,
      selectedZoneIdRef,
    ],
  );

  useEffect(() => {
    if (selectedTour <= 0 || selectedTourIdRef.current == null || !Array.isArray(tours)) return;
    const nextTourIndex = tours.findIndex((tour) => `${tour?.id}` === `${selectedTourIdRef.current}`);
    if (nextTourIndex >= 0 && nextTourIndex + 1 !== selectedTour) {
      setSelectedTour(nextTourIndex + 1);
    }
  }, [selectedTour, selectedTourIdRef, tours]);

  useEffect(() => {
    if (selectedZone <= 0 || selectedZoneIdRef.current == null || !Array.isArray(zones)) return;
    const nextZoneIndex = zones.findIndex((zone) => `${zone?.id}` === `${selectedZoneIdRef.current}`);
    if (nextZoneIndex >= 0 && nextZoneIndex + 1 !== selectedZone) {
      setSelectedZone(nextZoneIndex + 1);
    }
  }, [selectedZone, selectedZoneIdRef, zones]);

  useEffect(() => {
    if (activeEditSchedule || !initialPrefill || initialPrefill?.entityId == null) return;

    const prefillType = `${initialPrefill?.entityType || ''}`.toLowerCase();
    const prefillKey = `${prefillType}:${initialPrefill.entityId}:${initialPrefill.requestedAt || ''}`;
    if (appliedPrefillKeyRef.current === prefillKey) return;

    if (prefillType === 'tour') {
      if (!Array.isArray(tours)) return;
      const prefillNameKey = normalizePrefillLabel(
        initialPrefill?.entityName || initialPrefill?.entityData?.name,
      );
      const { hasNamePrefill, index: nextTourIndex, indexById: nextTourIndexById } =
        resolvePrefillEntityMatch(tours, initialPrefill.entityId, prefillNameKey);

      if (nextTourIndex < 0) {
        const fallbackTour =
          initialPrefill?.entityData && typeof initialPrefill.entityData === 'object'
            ? initialPrefill.entityData
            : null;
        const fallbackName =
          initialPrefill?.entityName || fallbackTour?.name || `Tour ${initialPrefill.entityId}`;
        const idCollision =
          nextTourIndexById >= 0 &&
          hasNamePrefill &&
          normalizePrefillLabel(tours[nextTourIndexById]?.name) !== prefillNameKey;

        if (!idCollision) {
          setTours((prevTours) => {
            const current = Array.isArray(prevTours) ? prevTours : [];
            if (current.some((tour) => `${tour?.id}` === `${initialPrefill.entityId}`)) {
              return current;
            }
            return [
              ...current,
              {
                id: initialPrefill.entityId,
                name: fallbackName,
                ...(fallbackTour || {}),
              },
            ];
          });
        }
        return;
      }
      selectTourOption(nextTourIndex + 1);
      if (!scheduleName.trim()) {
        const label = initialPrefill?.entityName || tours[nextTourIndex]?.name || 'Tour';
        setScheduleName(`${label} Schedule`);
      }
      appliedPrefillKeyRef.current = prefillKey;
      return;
    }

    if (prefillType === 'zone') {
      if (!Array.isArray(zones) || !zones.length) return;
      const prefillNameKey = normalizePrefillLabel(
        initialPrefill?.entityName || initialPrefill?.entityData?.name,
      );
      const { index: nextZoneIndex } = resolvePrefillEntityMatch(
        zones,
        initialPrefill.entityId,
        prefillNameKey,
      );
      if (nextZoneIndex < 0) return;
      selectZoneOption(nextZoneIndex + 1);
      if (!scheduleName.trim()) {
        const label = initialPrefill?.entityName || zones[nextZoneIndex]?.name || 'Zone';
        setScheduleName(`${label} Schedule`);
      }
      appliedPrefillKeyRef.current = prefillKey;
    }
  }, [
    activeEditSchedule,
    appliedPrefillKeyRef,
    initialPrefill,
    scheduleName,
    selectTourOption,
    selectZoneOption,
    setScheduleName,
    setTours,
    tours,
    zones,
  ]);

  return {
    selectedTour,
    setSelectedTour,
    selectedZone,
    setSelectedZone,
    selectTourOption,
    selectZoneOption,
  };
};
